import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import FranchiseList from '~/components/franchise/franchiseList/FranchiseList';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import useDebounce from '~/hooks/useDebounce';
import { handleApiError, notify } from '~/utils/helper';
import EditFranchiseDialog from '~/components/franchise/edit-franchise/EditFranchiseDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { ICreateFranchise, IPayloadDeleteFranchise } from '~/utils/interface/franchise';
import { franchiseActions } from '~/thunks/franchise/franchiseSlice';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import styles from './Franchise.module.scss';
import classNames from 'classnames/bind';
import usePermission from '~/hooks/usePermission';
import { handleGetFranchise, handleGetFranchiseDetail } from '~/thunks/franchise/franchiseThunk';
import { PERMISSIONS } from '~/utils/constants/permission';
import franchiseApi from '~/apis/franchise';
import { handleGetSelectionCompanies } from '~/thunks/company/companyThunk';

const cx = classNames.bind(styles);
const Franchise = () => {
  const [searchInput, setSearchInput] = useState<string>('');
  const searchInputDebounce = useDebounce<string>(searchInput);
  const permissions = usePermission();
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const [editData, setEditData] = useState<ICreateFranchise>();
  const { selectedIds, openAlertDelete, menuSelectedId, needRefreshData, filter } = useAppSelector(
    (state) => state.franchise
  );
  const dispatch = useAppDispatch();
  const { t } = useTranslation('superadmin/franchise');

  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        franchiseActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name'],
          page: 1,
        })
      );
    }
    // Prevent filter run twice times
  }, [searchInputDebounce, dispatch]);

  useEffect(() => {
    dispatch(handleGetFranchise(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filter, needRefreshData]);

  const handleOpenEdit = async (id: string) => {
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);
    try {
      const response = await dispatch(handleGetFranchiseDetail(id)).unwrap();
      setEditData(response);
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpen = async (open: boolean, _needRefreshData?: boolean) => {
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(open);
  };

  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(franchiseActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(franchiseActions.changeMenuSelectId(undefined));
    dispatch(franchiseActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedFranchises = useCallback(async () => {
    let payloadDeleteJobs: IPayloadDeleteFranchise =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await franchiseApi.deleteFranchise(payloadDeleteJobs);
      notify('success', t('deleteSuccessMessage'));
      dispatch(franchiseActions.changeNeedRefreshData(true));
      dispatch(franchiseActions.changeOpenAlertDelete(false));
      dispatch(handleGetSelectionCompanies());
    } catch (e: unknown) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, menuSelectedId, selectedIds, t]);

  return (
    <Box className='w-full h-full'>
      <BaseHeaderTitle title={t('title')} />
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
            <Button
              className={cx(
                { 'visibility: visible': selectedIds.length > 0 },
                { 'visibility: hidden': selectedIds.length <= 0 }
              )}
              onClick={openAlertDeleteModal}
              variant='contained'
              color='error'
              disableElevation
              startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
            >
              {t('delete')}
            </Button>
          </Box>
        </Box>

        <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
          {permissions[PERMISSIONS.company_create] && (
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpen}
            >
              {t('addfranchise')}
            </Button>
          )}

          <EditFranchiseDialog open={openEditDialog} setOpen={handleSetOpen} mode={editDialogMode} data={editData} />
        </Box>
      </Box>
      {permissions[PERMISSIONS.company_view] && <FranchiseList onOpenEdit={handleOpenEdit} />}

      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedFranchises}
      />
    </Box>
  );
};

export default Franchise;
